import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { isProjectId } from '../lib/project-path';

export type OrgRole = 'admin' | 'member';

export type ViewAs = { name: string; byEmail: string };

export type SessionSnapshot = {
  userId: string | null;
  email: string | null;
  roles: string[];
  orgRole: OrgRole | null;
  avatarUrl: string | null;
  starredProjectId: string | null;
  /** Set while a system admin is viewing Pulse as this person. */
  viewAs: ViewAs | null;
};

export type Session = SessionSnapshot & {
  ready: boolean;
  isAdmin: boolean;
  isOrgAdmin: boolean;
  reload: () => Promise<SessionSnapshot>;
};

function isOrgRole(value: unknown): value is OrgRole {
  return value === 'admin' || value === 'member';
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function emptySnapshot(): SessionSnapshot {
  return {
    userId: null,
    email: null,
    roles: [],
    orgRole: null,
    avatarUrl: null,
    starredProjectId: null,
    viewAs: null,
  };
}

function parseViewAs(value: unknown): ViewAs | null {
  if (!value || typeof value !== 'object') return null;
  const rec = value as { name?: unknown; by?: { email?: unknown } };
  if (typeof rec.name !== 'string') return null;
  return { name: rec.name, byEmail: typeof rec.by?.email === 'string' ? rec.by.email : '' };
}

function parseSession(data: unknown): SessionSnapshot {
  if (!data || typeof data !== 'object') {
    return emptySnapshot();
  }
  const rec = data as Record<string, unknown>;
  return {
    userId: typeof rec.id === 'string' ? rec.id : null,
    email: typeof rec.email === 'string' ? rec.email : null,
    roles: isStringArray(rec.roles) ? rec.roles : [],
    orgRole: isOrgRole(rec.orgRole) ? rec.orgRole : null,
    avatarUrl: typeof rec.avatarUrl === 'string' ? rec.avatarUrl : null,
    starredProjectId: isProjectId(rec.starredProjectId) ? rec.starredProjectId : null,
    viewAs: parseViewAs(rec.viewAs),
  };
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [snapshot, setSnapshot] = useState<SessionSnapshot>(emptySnapshot);

  const load = useCallback(async (): Promise<SessionSnapshot> => {
    try {
      const res = await fetch('/auth/me');
      if (!res.ok) {
        const empty = emptySnapshot();
        setSnapshot(empty);
        return empty;
      }
      const next = parseSession(await res.json());
      setSnapshot(next);
      return next;
    } catch {
      const empty = emptySnapshot();
      setSnapshot(empty);
      return empty;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, location.pathname]);

  const value = useMemo<Session>(
    () => ({
      ready,
      ...snapshot,
      isAdmin: snapshot.roles.includes('admin'),
      isOrgAdmin: snapshot.orgRole === 'admin',
      reload: load,
    }),
    [ready, snapshot, load],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
}
